import { alpha, createTheme } from '@mui/material/styles';

const fontFamily = [
  'Inter',
  'ui-sans-serif',
  'system-ui',
  '-apple-system',
  'BlinkMacSystemFont',
  '"Segoe UI"',
  'Roboto',
  'Arial',
  'sans-serif',
].join(',');

export const getTheme = (mode = 'light') => {
  const isLight = mode === 'light';
  const paper = isLight ? '#ffffff' : '#111827';
  const page = isLight ? '#f4f7fb' : '#0b1120';

  return createTheme({
    palette: {
      mode,
      primary: {
        main: '#2563eb',
        light: '#60a5fa',
        dark: '#1d4ed8',
        contrastText: '#ffffff',
      },
      secondary: {
        main: '#0f766e',
        light: '#14b8a6',
        dark: '#115e59',
      },
      success: { main: '#15803d' },
      warning: { main: '#d97706' },
      error: { main: '#dc2626' },
      info: { main: '#0284c7' },
      background: { default: page, paper },
      divider: isLight ? '#e2e8f0' : alpha('#94a3b8', 0.22),
      text: {
        primary: isLight ? '#172033' : '#f8fafc',
        secondary: isLight ? '#64748b' : '#94a3b8',
      },
    },
    // Los valores numéricos de `sx.borderRadius` se multiplican por esta base.
    // Una base de 8 mantiene tarjetas suaves sin convertir los paneles en cápsulas.
    shape: { borderRadius: 8 },
    typography: {
      fontFamily,
      h4: {
        fontWeight: 800,
        letterSpacing: '-0.035em',
        fontSize: 'clamp(1.75rem, 3vw, 2.35rem)',
      },
      h5: { fontWeight: 800, letterSpacing: '-0.025em' },
      h6: { fontWeight: 750, letterSpacing: '-0.015em' },
      subtitle1: { fontWeight: 700 },
      overline: { fontWeight: 800, letterSpacing: '0.09em', lineHeight: 1.5 },
      button: { fontWeight: 750, letterSpacing: 0, textTransform: 'none' },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          html: { backgroundColor: page },
          body: {
            minWidth: 320,
            backgroundColor: page,
            backgroundImage: isLight
              ? 'radial-gradient(circle at 0% 0%, rgba(37, 99, 235, 0.07), transparent 30%)'
              : 'radial-gradient(circle at 0% 0%, rgba(37, 99, 235, 0.15), transparent 28%)',
          },
          '*': { boxSizing: 'border-box' },
          '::selection': { backgroundColor: alpha('#2563eb', 0.22) },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backdropFilter: 'blur(14px)',
            backgroundColor: alpha(paper, isLight ? 0.82 : 0.72),
            boxShadow: `inset 0 -1px 0 ${alpha(isLight ? '#0f172a' : '#e2e8f0', 0.08)}`,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            border: `1px solid ${alpha(isLight ? '#cbd5e1' : '#94a3b8', isLight ? 0.62 : 0.18)}`,
            boxShadow: isLight
              ? '0 10px 30px rgba(15, 23, 42, 0.055)'
              : '0 12px 30px rgba(0, 0, 0, 0.2)',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            border: `1px solid ${alpha(isLight ? '#cbd5e1' : '#94a3b8', isLight ? 0.62 : 0.18)}`,
            boxShadow: isLight
              ? '0 10px 30px rgba(15, 23, 42, 0.055)'
              : '0 12px 30px rgba(0, 0, 0, 0.2)',
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            minHeight: 38,
            borderRadius: 10,
            paddingInline: 16,
          },
          contained: ({ theme }) => ({
            boxShadow: `0 8px 18px ${alpha(theme.palette.primary.main, 0.22)}`,
            '&:hover': {
              boxShadow: `0 10px 22px ${alpha(theme.palette.primary.main, 0.3)}`,
            },
          }),
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: 10,
            backgroundColor: alpha(theme.palette.background.paper, isLight ? 0.68 : 0.38),
            transition: theme.transitions.create(['border-color', 'box-shadow', 'background-color'], {
              duration: theme.transitions.duration.shortest,
            }),
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: alpha(theme.palette.primary.main, 0.46),
            },
            '&.Mui-focused': {
              boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.13)}`,
            },
          }),
          notchedOutline: ({ theme }) => ({
            borderColor: alpha(theme.palette.divider, 0.95),
          }),
        },
      },
      MuiInputLabel: {
        styleOverrides: { root: { fontWeight: 600 } },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 8, fontWeight: 700 },
          sizeSmall: { height: 24 },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: ({ theme }) => ({
            backgroundColor: isLight ? '#f8fafc' : alpha(theme.palette.background.paper, 0.95),
            color: theme.palette.text.secondary,
            fontSize: '0.71rem',
            fontWeight: 800,
            letterSpacing: '0.055em',
            textTransform: 'uppercase',
            borderBottom: `1px solid ${theme.palette.divider}`,
          }),
          root: ({ theme }) => ({
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.72)}`,
            paddingTop: 12,
            paddingBottom: 12,
          }),
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: ({ theme }) => ({
            '&:last-child td': { borderBottom: 0 },
            '&.MuiTableRow-hover:hover': {
              backgroundColor: alpha(theme.palette.primary.main, isLight ? 0.035 : 0.11),
            },
          }),
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: ({ theme }) => ({
            overflow: 'hidden',
            '&:before': { display: 'none' },
            border: `1px solid ${theme.palette.divider}`,
          }),
        },
      },
      MuiDialog: {
        styleOverrides: { paper: { borderRadius: 18 } },
      },
      MuiTooltip: {
        styleOverrides: { tooltip: { borderRadius: 8, fontSize: '0.75rem' } },
      },
    },
  });
};
